import { createContext, useContext, useState } from "react";
import { OrderImage } from "src/types";
import { View } from "../types/view";
import { views } from "../data/views";
import { UnbilledOrder } from "src/types/unbilled-order";

type OrderInvoicingState = {
    selectedOrder: UnbilledOrder | null;
    setSelectedOrder: (order: UnbilledOrder) => void;
    clearSelectedOrder: () => void;

    selectedImages: OrderImage[] | null;
    setSelectedImages: (images: OrderImage[]) => void;

    view: View;
    handleSetView: (view: string) => void;
};

const DEFAULT_CONTEXT: OrderInvoicingState = {
    selectedOrder: null,
    setSelectedOrder: () => {},
    clearSelectedOrder: () => {},

    selectedImages: null,
    setSelectedImages: () => {},

    view: views[0],
    handleSetView: () => {},
};

const OrderInvoicingContext =
    createContext<OrderInvoicingState>(DEFAULT_CONTEXT);

// Provider
export const OrderInvoicingProvider = ({
    children,
}: {
    children: React.ReactElement;
}) => {
    const [order, setOrder] = useState<UnbilledOrder | null>(null);
    const [images, setImages] = useState<OrderImage[] | null>(null);
    const [view, setView] = useState<View>(views[0]);

    function handleSetView(view: string) {
        const foundView = views.find((vc) => vc.viewName === view);

        if (foundView === undefined) {
            throw new Error(`${view} has no configuration`);
        }

        setView(foundView);
    }

    function handleClearSelectedOrder() {
        setOrder(null);
    }

    return (
        <OrderInvoicingContext.Provider
            value={{
                selectedOrder: order,
                setSelectedOrder: setOrder,
                clearSelectedOrder: handleClearSelectedOrder,

                selectedImages: images,
                setSelectedImages: setImages,

                view: view!,
                handleSetView,
            }}
        >
            {children}
        </OrderInvoicingContext.Provider>
    );
};

// Hook
export const useOrderInvoicing = () => {
    const context = useContext(OrderInvoicingContext);

    if (context === undefined) {
        throw new Error(
            "useOrderInvoicing must be used within a OrderInvoicingProvider",
        );
    }
    return context;
};
